import { useEffect, useMemo, useRef, useState } from "react";
import { gtApi } from "../../api/generalTradeApi";
import { useBusy } from "../../../context/BusyContext";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";
import "../../styles/generalTrade.css";

const UOMS = [
  "Unit",
  "Bag",
  "Gunni",
  "Roll",
  "Liters",
  "Packet",
  "Box",
  "Carton",
] as const;

type UomType = (typeof UOMS)[number];

interface CustomerOption {
  id: number;
  company_name: string;
  status: string;
  focal_person?: string | null;
  mobile_number?: string | null;
  phone_number?: string | null;
  email?: string | null;
}

interface OfflineProductOption {
  id: number;
  productName: string;
  SKU?: string | null;
  barCode?: string | null;

  actualPrice?: number | string | null;
  previousPrice?: number | string | null;
  sellingPrice?: number | string | null;
  activePrice?: "actual" | "previous" | "selling";
  active_price?: number | string | null;

  category_name?: string | null;
  subcategory_name?: string | null;
  brand_name?: string | null;
  product_type?: string | null;

  stock_status?: string | null;
  status?: string | null;
  image_path?: string | null;
}

interface SalesOrderFormData {
  customer_id: string;
  order_date: string;
  delivery_date: string;
  payment_date: string;
  remarks: string;
  status: "draft" | "submitted";
}

interface SalesOrderItem {
  row_id: string;
  product_id: number | "";
  product_label: string;
  product_name_snapshot: string;
  sku_snapshot: string;
  qty: number | string;
  uom: UomType;
  selling_price: number | string;
}

type DocumentMode = "add" | "edit" | "view";

interface Props {
  mode?: DocumentMode;
  salesOrderId?: number | null;
  onSaved: () => void;
  onCancel: () => void;
}

const createEmptyItem = (): SalesOrderItem => ({
  row_id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  product_id: "",
  product_label: "",
  product_name_snapshot: "",
  sku_snapshot: "",
  qty: 1,
  uom: "Unit",
  selling_price: 0,
});

const normalizeText = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getProductDisplayLabel = (product: OfflineProductOption): string => {
  const sku = product.SKU || "No SKU";
  const category = product.category_name ? ` • ${product.category_name}` : "";

  return `${product.productName} (${sku})${category}`;
};

const getProductPrice = (product: OfflineProductOption): number => {
  const price = Number(product.active_price ?? product.sellingPrice ?? 0);

  return Number.isFinite(price) ? price : 0;
};

export default function GeneralTradeDocumentForm({
  mode = "add",
  salesOrderId = null,
  onSaved,
  onCancel,
}: Props) {
  const { runBusy } = useBusy();
  const { showModal } = useMessageModal();
  const { formatDateForInput, getMalaysiaToday } = useFormat();

  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [products, setProducts] = useState<OfflineProductOption[]>([]);

  const [form, setForm] = useState<SalesOrderFormData>(() => ({
    customer_id: "",
    order_date: getMalaysiaToday(),
    delivery_date: getMalaysiaToday(),
    payment_date: getMalaysiaToday(),
    remarks: "",
    status: "draft",
  }));

  const [items, setItems] = useState<SalesOrderItem[]>([createEmptyItem()]);

  const [openProductRowId, setOpenProductRowId] = useState<string | null>(null);

  const [productSearchTerms, setProductSearchTerms] = useState<
    Record<string, string>
  >({});

  const [submitting, setSubmitting] = useState(false);

  const [documentNumber, setDocumentNumber] = useState("");

  const [loadingDocument, setLoadingDocument] = useState(false);

  const [serverLocked, setServerLocked] = useState(false);

  const [activeDeliveryOrderNumber, setActiveDeliveryOrderNumber] =
    useState("");

  const productDropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isAddMode = mode === "add";
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const formDisabled =
    submitting || loadingDocument || isViewMode || serverLocked;

  /* =========================
     LOAD CUSTOMERS + OFFLINE PRODUCTS
  ========================= */

  useEffect(() => {
    let cancelled = false;

    const loadInitialData = async (): Promise<void> => {
      try {
        setLoadingDocument(true);

        const customerRequest = gtApi.customers();

        const productRequest = gtApi.offlineProducts();

        const orderRequest =
          !isAddMode && salesOrderId
            ? gtApi.salesOrder(Number(salesOrderId))
            : Promise.resolve(null);

        const [customerRows, productRows, existingOrder] = await runBusy(() =>
          Promise.all([customerRequest, productRequest, orderRequest]),
        );

        if (cancelled) return;

        const safeCustomers = Array.isArray(customerRows) ? customerRows : [];

        const safeProducts = Array.isArray(productRows) ? productRows : [];

        setCustomers(safeCustomers);
        setProducts(safeProducts);

        if (!isAddMode && existingOrder) {
          const isLocked =
            Number(existingOrder.is_locked || 0) === 1 ||
            Number(existingOrder.is_editable ?? 1) === 0;

          setServerLocked(isLocked);

          setActiveDeliveryOrderNumber(
            existingOrder.active_delivery_order_number || "",
          );

          setDocumentNumber(existingOrder.sales_order_number || "");

          setForm({
            customer_id: String(existingOrder.customer_id || ""),

            order_date:
              formatDateForInput(existingOrder.order_date) ||
              getMalaysiaToday(),

            delivery_date:
              formatDateForInput(existingOrder.delivery_date) ||
              getMalaysiaToday(),

            payment_date:
              formatDateForInput(existingOrder.payment_date) ||
              getMalaysiaToday(),

            remarks: existingOrder.remarks || "",

            status:
              existingOrder.status === "submitted" ? "submitted" : "draft",
          });

          const existingItems = Array.isArray(existingOrder.items)
            ? existingOrder.items
            : [];

          const preparedItems: SalesOrderItem[] = existingItems.map(
            (item: any, index: number) => {
              const matchedProduct = safeProducts.find(
                (product: OfflineProductOption) =>
                  Number(product.id) === Number(item.product_id),
              );

              const productLabel = matchedProduct
                ? getProductDisplayLabel(matchedProduct)
                : `${item.product_name_snapshot || "Product"} (${
                    item.sku_snapshot || "No SKU"
                  })`;

              return {
                row_id: `${item.id || index}-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2)}`,
                product_id: Number(item.product_id),
                product_label: productLabel,
                product_name_snapshot:
                  item.product_name_snapshot ||
                  matchedProduct?.productName ||
                  "",
                sku_snapshot: item.sku_snapshot || matchedProduct?.SKU || "",
                qty: item.qty,
                uom: UOMS.includes(item.uom) ? item.uom : "Unit",
                selling_price: item.selling_price,
              };
            },
          );

          setItems(preparedItems.length ? preparedItems : [createEmptyItem()]);

          const searchValues = preparedItems.reduce<Record<string, string>>(
            (result, item) => {
              result[item.row_id] = item.product_label;

              return result;
            },
            {},
          );

          setProductSearchTerms(searchValues);

          if (isEditMode && isLocked) {
            showModal({
              type: "error",
              title: "Sales Order View Only",
              message: existingOrder.active_delivery_order_number
                ? `This Sales Order is linked to active Delivery Order ${existingOrder.active_delivery_order_number} and cannot be edited.`
                : "This Sales Order is linked to an active Delivery Order and cannot be edited.",
              confirmText: "OK",
            });
          }
        }
      } catch (error: any) {
        console.error("Failed to load Sales Order form:", error);

        if (!cancelled) {
          showModal({
            type: "error",
            title: "Unable to Load",
            message:
              error?.response?.data?.message ||
              "Unable to load the Sales Order form.",
            confirmText: "OK",
          });
        }
      } finally {
        if (!cancelled) {
          setLoadingDocument(false);
        }
      }
    };

    void loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [
    isAddMode,
    isEditMode,
    salesOrderId,
    runBusy,
    showModal,
    formatDateForInput,
    getMalaysiaToday,
  ]);

  /* =========================
     CLOSE PRODUCT DROPDOWN OUTSIDE
  ========================= */

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!openProductRowId) return;

      const activeDropdown = productDropdownRefs.current[openProductRowId];

      if (activeDropdown && !activeDropdown.contains(event.target as Node)) {
        setOpenProductRowId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openProductRowId]);

  /* =========================
     TOTALS
  ========================= */

  const total = useMemo(
    () =>
      items.reduce((sum, item) => {
        const qty = Number(item.qty || 0);
        const price = Number(item.selling_price || 0);

        return sum + qty * price;
      }, 0),
    [items],
  );

  /* =========================
     ITEM HELPERS
  ========================= */

  const updateItem = (rowId: string, patch: Partial<SalesOrderItem>) => {
    if (formDisabled) return;

    setItems((previous) =>
      previous.map((item) =>
        item.row_id === rowId
          ? {
              ...item,
              ...patch,
            }
          : item,
      ),
    );
  };

  const removeItem = (rowId: string) => {
    if (formDisabled) return;

    setItems((previous) => previous.filter((item) => item.row_id !== rowId));

    setProductSearchTerms((previous) => {
      const next = { ...previous };
      delete next[rowId];
      return next;
    });

    if (openProductRowId === rowId) {
      setOpenProductRowId(null);
    }
  };

  const addItem = () => {
    if (formDisabled) return;

    const newItem = createEmptyItem();

    setItems((previous) => [...previous, newItem]);

    setOpenProductRowId(newItem.row_id);

    setProductSearchTerms((previous) => ({
      ...previous,
      [newItem.row_id]: "",
    }));
  };

  const selectProduct = (rowId: string, product: OfflineProductOption) => {
    if (formDisabled) return;

    const duplicateExists = items.some(
      (item) =>
        item.row_id !== rowId && Number(item.product_id) === Number(product.id),
    );

    if (duplicateExists) {
      showModal({
        type: "error",
        title: "Product Already Added",
        message:
          "This product has already been added to the Sales Order. Please update its quantity instead.",
        confirmText: "OK",
      });

      return;
    }

    const displayLabel = getProductDisplayLabel(product);

    updateItem(rowId, {
      product_id: product.id,
      product_label: displayLabel,
      product_name_snapshot: product.productName,
      sku_snapshot: product.SKU || "",
      selling_price: getProductPrice(product),
    });

    setProductSearchTerms((previous) => ({
      ...previous,
      [rowId]: displayLabel,
    }));

    setOpenProductRowId(null);
  };

  const clearSelectedProduct = (rowId: string) => {
    updateItem(rowId, {
      product_id: "",
      product_label: "",
      product_name_snapshot: "",
      sku_snapshot: "",
      selling_price: 0,
    });

    setProductSearchTerms((previous) => ({
      ...previous,
      [rowId]: "",
    }));

    setOpenProductRowId(rowId);
  };

  const getFilteredProducts = (rowId: string): OfflineProductOption[] => {
    const keyword = normalizeText(productSearchTerms[rowId]);

    if (!keyword) {
      return products;
    }

    return products.filter((product) => {
      const searchableValues = [
        product.productName,
        product.SKU,
        product.barCode,
        product.category_name,
        product.subcategory_name,
        product.brand_name,
        product.product_type,
      ];

      return searchableValues.some((value) =>
        normalizeText(value).includes(keyword),
      );
    });
  };

  /* =========================
     SUBMIT
  ========================= */

  const submit = async (targetStatus: "draft" | "submitted"): Promise<void> => {
    if (isViewMode || serverLocked) {
      return;
    }

    if (!form.customer_id) {
      showModal({
        type: "error",
        title: "Customer Required",
        message: "Please select a customer before submitting the Sales Order.",
        confirmText: "OK",
      });

      return;
    }

    if (!form.order_date) {
      showModal({
        type: "error",
        title: "Order Date Required",
        message: "Please select the Order Date.",
        confirmText: "OK",
      });

      return;
    }

    if (!form.delivery_date) {
      showModal({
        type: "error",
        title: "Delivery Date Required",
        message: "Please select the Delivery Date.",
        confirmText: "OK",
      });

      return;
    }

    if (!form.payment_date) {
      showModal({
        type: "error",
        title: "Payment Date Required",
        message: "Please select the Payment Date.",
        confirmText: "OK",
      });

      return;
    }

    const invalidItemIndex = items.findIndex(
      (item) =>
        !item.product_id ||
        Number(item.qty) <= 0 ||
        Number(item.selling_price) < 0,
    );

    if (invalidItemIndex !== -1) {
      showModal({
        type: "error",
        title: "Incomplete Product Line",
        message: `Please complete Product #${
          invalidItemIndex + 1
        } before submitting the Sales Order.`,
        confirmText: "OK",
      });

      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        customer_id: Number(form.customer_id),
        order_date: form.order_date,
        delivery_date: form.delivery_date,
        payment_date: form.payment_date,
        remarks: form.remarks.trim(),
        status: targetStatus,
        items: items.map((item) => ({
          product_id: Number(item.product_id),
          product_name_snapshot: item.product_name_snapshot,
          sku_snapshot: item.sku_snapshot || null,
          qty: Number(item.qty),
          uom: item.uom,
          selling_price: Number(item.selling_price),
          subtotal: Number(
            (Number(item.qty) * Number(item.selling_price)).toFixed(2),
          ),
        })),
      };

      const response = await runBusy(() => {
        if (isEditMode && salesOrderId) {
          return gtApi.updateSalesOrder(Number(salesOrderId), payload);
        }

        return gtApi.createSalesOrder(payload);
      });

      showModal({
        type: "success",
        title: isEditMode ? "Sales Order Updated" : "Sales Order Created",
        message:
          response?.message ||
          (isEditMode
            ? "Sales Order updated successfully."
            : "Sales Order created successfully."),
        confirmText: "OK",
      });

      onSaved();
    } catch (error: any) {
      console.error("Create Sales Order Error:", error);

      showModal({
        type: "error",
        title: "Unable to Submit",
        message:
          error?.response?.data?.message ||
          (isEditMode
            ? "Unable to update the Sales Order."
            : "Unable to create the Sales Order."),
        confirmText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gt-card">
      <div className="gt-document-title-row">
        <div>
          <h2>
            {isAddMode
              ? "NEW SALES ORDER"
              : isEditMode
                ? "EDIT SALES ORDER"
                : "VIEW SALES ORDER"}
          </h2>

          {documentNumber && (
            <span className="gt-document-number">{documentNumber}</span>
          )}
        </div>

        {(isViewMode || serverLocked) && activeDeliveryOrderNumber && (
          <div className="gt-document-lock-message">
            Linked to active Delivery Order{" "}
            <strong>{activeDeliveryOrderNumber}</strong>. This Sales Order is
            view-only.
          </div>
        )}
      </div>

      {/* =========================
          DOCUMENT HEADER
      ========================= */}

      <div className="gt-form-grid">
        <label>
          Customer
          <select
            value={form.customer_id}
            disabled={formDisabled}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                customer_id: event.target.value,
              }))
            }
          >
            <option value="">Select Customer</option>

            {customers
              .filter((customer) => customer.status === "active")
              .map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.company_name}
                </option>
              ))}
          </select>
        </label>

        <label>
          Order Date
          <input
            type="date"
            value={form.order_date}
            disabled={formDisabled}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                order_date: event.target.value,
              }))
            }
          />
        </label>

        <label>
          Delivery Date
          <input
            type="date"
            value={form.delivery_date}
            disabled={formDisabled}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                delivery_date: event.target.value,
              }))
            }
          />
        </label>

        <label>
          Payment Date
          <input
            type="date"
            value={form.payment_date}
            disabled={formDisabled}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                payment_date: event.target.value,
              }))
            }
          />
        </label>
      </div>

      {/* =========================
          PRODUCT LINES
      ========================= */}

      <div className="gt-lines">
        {items.map((item, index) => {
          const filteredProducts = getFilteredProducts(item.row_id);

          return (
            <div className="gt-line-card" key={item.row_id}>
              <div className="gt-line-head">
                <strong>Product #{index + 1}</strong>

                {items.length > 1 && !formDisabled && (
                  <button
                    type="button"
                    className="gt-remove-line-btn"
                    onClick={() => removeItem(item.row_id)}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="gt-line-grid">
                <label className="gt-product-field">
                  Product
                  <div
                    className="gt-search-select"
                    ref={(element) => {
                      productDropdownRefs.current[item.row_id] = element;
                    }}
                  >
                    <div className="gt-search-select-control">
                      <input
                        type="text"
                        disabled={formDisabled}
                        autoComplete="off"
                        placeholder="Search product, SKU, barcode, category..."
                        value={
                          productSearchTerms[item.row_id] ??
                          item.product_label ??
                          ""
                        }
                        onFocus={() => setOpenProductRowId(item.row_id)}
                        onChange={(event) => {
                          const value = event.target.value;

                          setProductSearchTerms((previous) => ({
                            ...previous,
                            [item.row_id]: value,
                          }));

                          if (item.product_id) {
                            updateItem(item.row_id, {
                              product_id: "",
                              product_label: "",
                              product_name_snapshot: "",
                              sku_snapshot: "",
                              selling_price: 0,
                            });
                          }

                          setOpenProductRowId(item.row_id);
                        }}
                      />

                      {item.product_id ? (
                        <button
                          type="button"
                          disabled={formDisabled}
                          className="gt-search-select-clear"
                          onClick={() => clearSelectedProduct(item.row_id)}
                          aria-label="Clear selected product"
                        >
                          ×
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="gt-search-select-toggle"
                          onClick={() =>
                            setOpenProductRowId(
                              openProductRowId === item.row_id
                                ? null
                                : item.row_id,
                            )
                          }
                          aria-label="Open product list"
                        >
                          ▾
                        </button>
                      )}
                    </div>

                    {openProductRowId === item.row_id && (
                      <div className="gt-search-select-menu">
                        <div className="gt-search-select-summary">
                          {filteredProducts.length} Offline product
                          {filteredProducts.length === 1 ? "" : "s"} found
                        </div>

                        <div className="gt-search-select-options">
                          {filteredProducts.length > 0 ? (
                            filteredProducts.map((product) => {
                              const alreadySelected = items.some(
                                (otherItem) =>
                                  otherItem.row_id !== item.row_id &&
                                  Number(otherItem.product_id) ===
                                    Number(product.id),
                              );

                              return (
                                <button
                                  key={product.id}
                                  type="button"
                                  className={`gt-search-select-option ${
                                    alreadySelected ? "disabled" : ""
                                  }`}
                                  disabled={alreadySelected}
                                  onClick={() =>
                                    selectProduct(item.row_id, product)
                                  }
                                >
                                  <div className="gt-product-option-main">
                                    <strong>{product.productName}</strong>

                                    <span>
                                      RM {getProductPrice(product).toFixed(2)}
                                    </span>
                                  </div>

                                  <div className="gt-product-option-meta">
                                    <span>SKU: {product.SKU || "-"}</span>

                                    <span>
                                      Barcode: {product.barCode || "-"}
                                    </span>

                                    <span>
                                      Category: {product.category_name || "-"}
                                    </span>

                                    <span>
                                      Subcategory:{" "}
                                      {product.subcategory_name || "-"}
                                    </span>

                                    {product.brand_name && (
                                      <span>Brand: {product.brand_name}</span>
                                    )}

                                    {alreadySelected && (
                                      <span className="gt-product-already-added">
                                        Already added
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          ) : (
                            <div className="gt-search-select-empty">
                              No matching Offline products found.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </label>

                <label>
                  Qty
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={item.qty}
                    disabled={formDisabled}
                    onChange={(event) =>
                      updateItem(item.row_id, {
                        qty: event.target.value,
                      })
                    }
                  />
                </label>

                <label>
                  UOM
                  <select
                    value={item.uom}
                    disabled={formDisabled}
                    onChange={(event) =>
                      updateItem(item.row_id, {
                        uom: event.target.value as UomType,
                      })
                    }
                  >
                    {UOMS.map((uom) => (
                      <option key={uom} value={uom}>
                        {uom}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Selling Price
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.selling_price}
                    disabled={formDisabled}
                    onChange={(event) =>
                      updateItem(item.row_id, {
                        selling_price: event.target.value,
                      })
                    }
                  />
                </label>

                <label>
                  Subtotal
                  <input
                    disabled
                    value={(
                      Number(item.qty || 0) * Number(item.selling_price || 0)
                    ).toFixed(2)}
                  />
                </label>
              </div>
            </div>
          );
        })}

        {!formDisabled && (
          <button
            type="button"
            className="btn-secondary gt-add-product-btn"
            onClick={addItem}
          >
            + Add Product
          </button>
        )}
      </div>

      {/* =========================
          REMARKS
      ========================= */}

      <label>
        Remarks
        <textarea
          value={form.remarks}
          disabled={formDisabled}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              remarks: event.target.value,
            }))
          }
        />
      </label>

      {/* =========================
          FOOTER
      ========================= */}

      <div className="gt-footer">
        <div>
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            {isViewMode || serverLocked ? "Back" : "Cancel"}
          </button>

          {!isViewMode && !serverLocked && (
            <>
              <button
                type="button"
                className="btn-secondary"
                disabled={submitting || loadingDocument}
                onClick={() => {
                  setForm((previous) => ({
                    ...previous,
                    status: "draft",
                  }));

                  void submit("draft");
                }}
              >
                {submitting && form.status === "draft"
                  ? "Saving..."
                  : "Save Draft"}
              </button>

              <button
                type="button"
                className="btn-primary"
                disabled={submitting || loadingDocument}
                onClick={() => {
                  setForm((previous) => ({
                    ...previous,
                    status: "submitted",
                  }));

                  void submit("submitted");
                }}
              >
                {submitting && form.status === "submitted"
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                    ? "Update & Submit"
                    : "Submit Order"}
              </button>
            </>
          )}
        </div>

        <strong>Grand Total: RM {total.toFixed(2)}</strong>
      </div>
    </div>
  );
}
